/**
 * Contacts et listes de diffusion.
 *
 * Une liste n'est pas un sac de numéros : elle porte la **base légale** de
 * l'envoi. L'écran met cette base au premier plan, parce que c'est elle qui
 * décide si une campagne publicitaire est licite — loi 025/2023.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type BaseLegale,
  type ContactListe,
  type ListeDiffusion,
  type ResultatImport,
} from '../../api/client';
import {
  Bandeau,
  Carte,
  Chargement,
  EtatVide,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import { formaterDate, formaterNombre, formaterNumero } from '../../lib/format';

const BASES: {
  valeur: BaseLegale;
  libelle: string;
  publicitaire: boolean;
  explication: string;
}[] = [
  {
    valeur: 'opt_in',
    libelle: 'Opt-in horodaté',
    publicitaire: true,
    explication:
      'Formulaire web, case décochée par défaut. La preuve est l’enregistrement horodaté du consentement.',
  },
  {
    valeur: 'paper_form',
    libelle: 'Formulaire papier',
    publicitaire: true,
    explication:
      'Bulletin signé, conservé par vos soins. Indiquez sa référence : c’est elle qu’on présente en cas de contrôle.',
  },
  {
    valeur: 'contractual',
    libelle: 'Relation contractuelle',
    publicitaire: false,
    explication:
      'Autorise le transactionnel — code de validation, alerte de solde, relevé. N’autorise jamais la publicité : une relation client ne vaut pas consentement commercial.',
  },
  {
    valeur: 'undocumented',
    libelle: 'Non documentée',
    publicitaire: false,
    explication:
      'Aucune preuve déclarée. La liste est utilisable en transactionnel ; toute campagne publicitaire sera refusée.',
  },
];

const baseDe = (valeur: BaseLegale) => BASES.find((b) => b.valeur === valeur) ?? BASES[3]!;

/** Une liste sans mouvement depuis six mois mérite une relecture. */
const DORMANTE_JOURS = 180;

const estDormante = (liste: ListeDiffusion) =>
  Date.now() - new Date(liste.updated_at).getTime() > DORMANTE_JOURS * 86_400_000;

interface Brouillon {
  id?: string;
  name: string;
  description: string;
  legal_basis: BaseLegale;
  proof: string;
  collected_at: string;
}

const BROUILLON_VIDE: Brouillon = {
  name: '',
  description: '',
  legal_basis: 'opt_in',
  proof: '',
  collected_at: '',
};

export function Contacts() {
  const [listes, setListes] = useState<ListeDiffusion[] | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactListe[] | null>(null);
  const [total, setTotal] = useState(0);
  const [recherche, setRecherche] = useState('');
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [csv, setCsv] = useState('');
  const [resultatImport, setResultatImport] = useState<ResultatImport | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);
  const champFichier = useRef<HTMLInputElement>(null);
  const zoneImport = useRef<HTMLDivElement>(null);

  /**
   * Ouvre une liste et amène l'import sous les yeux.
   *
   * Le panneau d'import n'existe qu'une fois une liste ouverte, et la seule
   * façon de l'ouvrir était de cliquer le nom de la liste — qui ne ressemble
   * pas à un lien. La fonction était donc introuvable : on nous a demandé de
   * l'ajouter alors qu'elle existait. Un bouton explicite par ligne la rend
   * visible, et le défilement évite de chercher où la page a changé.
   */
  function ouvrirImport(id: string) {
    setOuverte(id);
    // Au prochain rendu : la zone n'existe pas encore quand la liste s'ouvre.
    requestAnimationFrame(() =>
      zoneImport.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    );
  }

  const chargerListes = useCallback(async () => {
    try {
      const { data } = await api.listes();
      setListes(data);
    } catch (e) {
      setErreur(e);
    }
  }, []);

  useEffect(() => {
    void chargerListes();
  }, [chargerListes]);

  const chargerContacts = useCallback(async (listId: string, filtre: string) => {
    try {
      const { data, total: n } = await api.contactsDeLaListe(listId, filtre || undefined);
      setContacts(data);
      setTotal(n);
    } catch (e) {
      setErreur(e);
    }
  }, []);

  useEffect(() => {
    if (!ouverte) {
      setContacts(null);
      return;
    }
    const minuterie = setTimeout(() => void chargerContacts(ouverte, recherche), 250);
    return () => clearTimeout(minuterie);
  }, [ouverte, recherche, chargerContacts]);

  async function enregistrer() {
    if (!brouillon) return;
    setErreur(null);
    setEnCours(true);
    try {
      const charge = {
        name: brouillon.name.trim(),
        description: brouillon.description.trim() || null,
        legal_basis: brouillon.legal_basis,
        proof: brouillon.proof.trim() || null,
        collected_at: brouillon.collected_at
          ? new Date(brouillon.collected_at).toISOString()
          : null,
      };
      if (brouillon.id) await api.modifierListe(brouillon.id, charge);
      else await api.creerListe(charge);
      setBrouillon(null);
      await chargerListes();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  async function supprimer(liste: ListeDiffusion) {
    setErreur(null);
    try {
      await api.supprimerListe(liste.id);
      if (ouverte === liste.id) setOuverte(null);
      await chargerListes();
    } catch (e) {
      setErreur(e);
    }
  }

  async function importer() {
    if (!ouverte) return;
    setErreur(null);
    setResultatImport(null);
    setEnCours(true);
    try {
      const resultat = await api.importer(ouverte, csv);
      setResultatImport(resultat);
      setCsv('');
      if (champFichier.current) champFichier.current.value = '';
      await Promise.all([chargerListes(), chargerContacts(ouverte, recherche)]);
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  // Le fichier est lu dans le navigateur puis envoyé en texte : pas de
  // téléversement multipart à gérer côté serveur pour un gain nul.
  function lireFichier(fichier: File | undefined) {
    if (!fichier) return;
    const lecteur = new FileReader();
    lecteur.onload = () => setCsv(String(lecteur.result ?? ''));
    lecteur.readAsText(fichier, 'utf-8');
  }

  if (listes === null) return <Chargement quoi="Chargement des listes…" />;

  const listeOuverte = listes.find((l) => l.id === ouverte) ?? null;

  return (
    <>
      <MessageErreur erreur={erreur} />

      <Bandeau titre="Une liste porte la base légale de l’envoi, pas seulement des numéros.">
        La loi 025/2023 exige un consentement <strong>prouvable</strong> pour tout envoi
        publicitaire. Une liste dont la base est « relation contractuelle » ou « non documentée »
        reste utilisable en transactionnel — code de validation, alerte, relevé — mais toute
        campagne commerciale y sera refusée.
      </Bandeau>

      {/* --- Formulaire de liste --- */}
      {brouillon && (
        <div style={{ marginBottom: 16 }}>
          <Carte titre={brouillon.id ? 'Modifier la liste' : 'Nouvelle liste'}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
              <label className="champ" style={{ flex: '1 1 240px' }}>
                <span>Nom de la liste</span>
                <input
                  type="text"
                  value={brouillon.name}
                  onChange={(e) => setBrouillon({ ...brouillon, name: e.target.value })}
                  placeholder="Clients EMF — comptes actifs"
                />
              </label>
              <label className="champ" style={{ flex: '1 1 240px' }}>
                <span>Base légale</span>
                <select
                  value={brouillon.legal_basis}
                  onChange={(e) =>
                    setBrouillon({ ...brouillon, legal_basis: e.target.value as BaseLegale })
                  }
                >
                  {BASES.map((b) => (
                    <option key={b.valeur} value={b.valeur}>
                      {b.libelle}
                      {b.publicitaire ? '' : ' — transactionnel seulement'}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="aide" style={{ marginTop: 0, marginBottom: 14 }}>
              {baseDe(brouillon.legal_basis).explication}
            </div>

            {brouillon.legal_basis !== 'undocumented' && (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
                <label className="champ" style={{ flex: '1 1 260px' }}>
                  <span>Référence de la preuve</span>
                  <input
                    type="text"
                    value={brouillon.proof}
                    onChange={(e) => setBrouillon({ ...brouillon, proof: e.target.value })}
                    placeholder="Formulaire web F-2026-114, contrat n° 4417…"
                  />
                  <div className="aide">
                    Obligatoire : une base déclarée sans preuve n’a aucune valeur devant un
                    contrôle.
                  </div>
                </label>
                <label className="champ" style={{ flex: '0 1 200px' }}>
                  <span>Date de collecte</span>
                  <input
                    type="date"
                    value={brouillon.collected_at}
                    onChange={(e) => setBrouillon({ ...brouillon, collected_at: e.target.value })}
                  />
                </label>
              </div>
            )}

            <label className="champ" style={{ marginBottom: 18 }}>
              <span>Description</span>
              <input
                type="text"
                value={brouillon.description}
                onChange={(e) => setBrouillon({ ...brouillon, description: e.target.value })}
                placeholder="Origine, périmètre, personne responsable…"
              />
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="primaire"
                onClick={enregistrer}
                disabled={
                  enCours ||
                  !brouillon.name.trim() ||
                  (brouillon.legal_basis !== 'undocumented' && !brouillon.proof.trim())
                }
              >
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button className="secondaire" onClick={() => setBrouillon(null)}>
                Annuler
              </button>
            </div>
          </Carte>
        </div>
      )}

      {/* --- Listes --- */}
      <Carte
        titre={`Listes de diffusion — ${listes.length}`}
        action={
          !brouillon && (
            <button className="accent" onClick={() => setBrouillon({ ...BROUILLON_VIDE })}>
              Nouvelle liste
            </button>
          )
        }
        sansCorps
      >
        {listes.length === 0 ? (
          <EtatVide titre="Aucune liste pour l’instant.">
            <div className="aide" style={{ marginTop: 0 }}>
              Créez une liste, déclarez sa base légale, puis importez vos contacts au format CSV.
            </div>
          </EtatVide>
        ) : (
          <div className="tableau-conteneur">
            <table style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th>Liste</th>
                  <th>Base légale</th>
                  <th className="droite">Contacts</th>
                  <th className="droite">Retraits STOP</th>
                  <th className="droite">Joignables</th>
                  <th>Mise à jour</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {listes.map((liste) => (
                  <tr
                    key={liste.id}
                    // La ligne ouverte est teintée : sans cela, rien ne relie le
                    // panneau qui apparaît plus bas à la liste qu'il concerne.
                    style={ouverte === liste.id ? { background: 'var(--puce-ok-fond)' } : undefined}
                  >
                    <td>
                      <button
                        className="lien"
                        onClick={() => setOuverte(ouverte === liste.id ? null : liste.id)}
                        style={{ fontWeight: 500 }}
                      >
                        {liste.name}
                      </button>
                      {liste.description && (
                        <div style={{ color: 'var(--muet)', fontSize: 12 }}>
                          {liste.description}
                        </div>
                      )}
                      {liste.proof && (
                        <div style={{ color: 'var(--muet)', fontSize: 11.5 }}>
                          Preuve : {liste.proof}
                        </div>
                      )}
                    </td>
                    <td>
                      <PuceLibre ton={liste.allows_marketing ? 'succes' : 'attente'}>
                        {liste.legal_basis_label}
                      </PuceLibre>
                      {!liste.allows_marketing && (
                        <div style={{ color: 'var(--muet)', fontSize: 11.5, marginTop: 4 }}>
                          Transactionnel seulement
                        </div>
                      )}
                    </td>
                    <td className="nombre">{formaterNombre(liste.contacts_total)}</td>
                    <td
                      className="nombre"
                      style={{ color: liste.contacts_opted_out > 0 ? 'var(--rouge)' : undefined }}
                    >
                      {formaterNombre(liste.contacts_opted_out)}
                    </td>
                    <td className="nombre" style={{ fontWeight: 500 }}>
                      {formaterNombre(liste.contacts_reachable)}
                    </td>
                    <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                      {formaterDate(liste.updated_at)}
                      {estDormante(liste) && (
                        <div style={{ color: 'var(--orange-fonce)', fontSize: 11.5 }}>
                          Dormante depuis plus de 6 mois
                        </div>
                      )}
                    </td>
                    <td className="droite" style={{ whiteSpace: 'nowrap' }}>
                      <button className="accent petit" onClick={() => ouvrirImport(liste.id)}>
                        Importer
                      </button>{' '}
                      <button
                        className="secondaire petit"
                        onClick={() =>
                          setBrouillon({
                            id: liste.id,
                            name: liste.name,
                            description: liste.description ?? '',
                            legal_basis: liste.legal_basis,
                            proof: liste.proof ?? '',
                            collected_at: liste.collected_at?.slice(0, 10) ?? '',
                          })
                        }
                      >
                        Modifier
                      </button>{' '}
                      <button className="secondaire petit" onClick={() => supprimer(liste)}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="corps" style={{ paddingTop: 12 }}>
          <div className="aide" style={{ marginTop: 0 }}>
            Les retraits STOP sont <strong>dérivés</strong> à chaque affichage, jamais stockés : un
            compteur figé dériverait dès le premier STOP reçu, et personne ne s’en apercevrait avant
            un contrôle. La colonne « joignables » est ce qui partira réellement.
          </div>
        </div>
      </Carte>

      {/* --- Contacts de la liste ouverte --- */}
      {listeOuverte && (
        <div style={{ marginTop: 16 }}>
          <div className="rangee">
            <div className="large">
              <Carte
                titre={`Contacts — ${listeOuverte.name}`}
                action={
                  <input
                    type="text"
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Rechercher un numéro"
                    style={{ width: 220 }}
                  />
                }
                sansCorps
              >
                {contacts === null ? (
                  <Chargement />
                ) : contacts.length === 0 ? (
                  <EtatVide
                    titre={
                      recherche
                        ? 'Aucun contact ne correspond à cette recherche.'
                        : 'Cette liste est vide — importez un fichier CSV.'
                    }
                  />
                ) : (
                  <div className="tableau-conteneur">
                    <table style={{ minWidth: 560 }}>
                      <thead>
                        <tr>
                          <th>Numéro</th>
                          <th>Prénom</th>
                          <th>Variables</th>
                          <th>État</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((contact) => (
                          <tr key={contact.id}>
                            <td className="mono" style={{ fontSize: 12.5 }}>
                              {formaterNumero(contact.to)}
                            </td>
                            <td>{contact.first_name ?? '—'}</td>
                            <td style={{ color: 'var(--muet)', fontSize: 12 }}>
                              {contact.attributes
                                ? Object.entries(contact.attributes)
                                    .map(([k, v]) => `${k}=${v}`)
                                    .join(' · ')
                                : '—'}
                            </td>
                            <td>
                              {contact.opted_out ? (
                                <PuceLibre ton="echec">Retiré (STOP)</PuceLibre>
                              ) : (
                                <PuceLibre ton="succes">Joignable</PuceLibre>
                              )}
                            </td>
                            <td className="droite">
                              <button
                                className="secondaire petit"
                                onClick={async () => {
                                  await api.supprimerContact(contact.id).catch(setErreur);
                                  await Promise.all([
                                    chargerListes(),
                                    chargerContacts(listeOuverte.id, recherche),
                                  ]);
                                }}
                              >
                                Retirer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="corps" style={{ paddingTop: 12 }}>
                  <div className="aide" style={{ marginTop: 0 }}>
                    {formaterNombre(total)} contact(s) au total.
                    {contacts && contacts.length < total
                      ? ` ${formaterNombre(contacts.length)} affiché(s) — affinez la recherche.`
                      : ''}{' '}
                    Retirer un contact de la liste ne l’inscrit pas sur la liste STOP : ce sont deux
                    gestes distincts, et seul le second est opposable.
                  </div>
                </div>
              </Carte>
            </div>

            <div className="etroit" ref={zoneImport}>
              <Carte titre="Importer des contacts">
                <label className="champ" style={{ marginBottom: 12 }}>
                  <span>Fichier CSV</span>
                  <input
                    ref={champFichier}
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain"
                    onChange={(e) => lireFichier(e.target.files?.[0])}
                  />
                </label>

                <label className="champ" style={{ marginBottom: 12 }}>
                  <span>… ou collez directement</span>
                  <textarea
                    rows={6}
                    value={csv}
                    onChange={(e) => setCsv(e.target.value)}
                    placeholder={
                      'numero;prenom\n+241 66 00 12 42;Aline\n+241 77 00 12 43;Serge\n\n… ou : 077280150, 066001242, 074123456'
                    }
                    spellCheck={false}
                  />
                  <div className="aide">
                    Une colonne de numéros suffit, ou une simple liste séparée par des
                    virgules. Un en-tête <span className="mono">numero</span>,{' '}
                    <span className="mono">telephone</span> ou <span className="mono">msisdn</span>{' '}
                    est reconnu, ainsi que <span className="mono">prenom</span>. Les colonnes
                    restantes deviennent des variables de personnalisation. Séparateur détecté
                    automatiquement. Toutes les écritures d’un numéro sont acceptées — les
                    préfixes non attribués et les lignes fixes sont rejetés, motif à l’appui.
                  </div>
                </label>

                <button className="primaire" onClick={importer} disabled={enCours || !csv.trim()}>
                  {enCours ? 'Import…' : 'Importer'}
                </button>

                {listeOuverte.allows_marketing && (
                  <div className="aide">
                    Chaque contact importé sera inscrit au registre de consentement avec la preuve «{' '}
                    {listeOuverte.proof} ». C’est cette écriture, et non la ligne de contact, qui
                    sera présentée en cas de contrôle.
                  </div>
                )}
              </Carte>
            </div>
          </div>

          {resultatImport && (
            <div style={{ marginTop: 16 }}>
              <Carte titre="Résultat de l’import">
                <dl className="definitions">
                  <dt>Lignes lues</dt>
                  <dd>{formaterNombre(resultatImport.lignesLues)}</dd>
                  <dt>Importés</dt>
                  <dd style={{ color: 'var(--vert)', fontWeight: 500 }}>
                    {formaterNombre(resultatImport.importes)}
                  </dd>
                  <dt>Déjà présents</dt>
                  <dd>{formaterNombre(resultatImport.dejaPresents)}</dd>
                  <dt>Doublons du fichier</dt>
                  <dd>{formaterNombre(resultatImport.doublonsFichier)}</dd>
                  <dt>Rejetés</dt>
                  <dd
                    style={{
                      color: resultatImport.rejetees.length ? 'var(--rouge)' : undefined,
                    }}
                  >
                    {formaterNombre(resultatImport.rejetees.length)}
                  </dd>
                  {resultatImport.consentementsEnregistres > 0 && (
                    <>
                      <dt>Consentements</dt>
                      <dd>{formaterNombre(resultatImport.consentementsEnregistres)}</dd>
                    </>
                  )}
                </dl>

                {resultatImport.rejetees.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="libelle-champ">Lignes rejetées</div>
                    <div className="tableau-conteneur" style={{ maxHeight: 240 }}>
                      <table>
                        <thead>
                          <tr>
                            <th className="droite">Ligne</th>
                            <th>Valeur</th>
                            <th>Motif</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultatImport.rejetees.map((r) => (
                            <tr key={`${r.ligne}-${r.valeur}`}>
                              <td className="nombre">{r.ligne}</td>
                              <td className="mono" style={{ fontSize: 12 }}>
                                {r.valeur || '(vide)'}
                              </td>
                              <td style={{ color: 'var(--rouge)', fontSize: 12.5 }}>{r.motif}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="aide">
                      Le numéro de ligne est celui de votre tableur : corrigez le fichier et
                      réimportez. Un réimport n’ajoute jamais de doublon.
                    </div>
                  </div>
                )}
              </Carte>
            </div>
          )}
        </div>
      )}

      {resultatImport && !listeOuverte && (
        <MessageSucces>Import terminé — rouvrez la liste pour en voir le détail.</MessageSucces>
      )}
    </>
  );
}
